import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { DndContext, DragOverlay, useDroppable, useDraggable, PointerSensor, TouchSensor, useSensors, useSensor, closestCenter, rectIntersection } from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent, CollisionDetection } from '@dnd-kit/core';
import { toast } from 'sonner';
import { api } from '../api/client';
import {
  LayoutDashboard,
  Calendar,
  Plus,
  MapPin,
  Wrench,
  Users,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Search,
  Loader,
  Pencil,
  Play,
  Ticket,
  Phone,
  XCircle,
  FileText,
  CheckCircle,
  ClipboardList,
  AlertTriangle,
  RotateCcw,
  RotateCw
} from 'lucide-react';
import { format, addDays, startOfWeek, addWeeks, startOfMonth, endOfMonth, addMonths, subMonths } from 'date-fns';
import { parseCalendarDate, formatCalendarDate, scheduledTimeOfDay, calendarDayString } from '../lib/calendar-date';
import { useAuth } from '../contexts/AuthContext';
import { useIndustry } from '../contexts/IndustryContext';
import { getJobTypeLabel } from '../constants/job-types';
import { jobStatusStyle, jobStatusStripe } from '../lib/job-status';
import { cardsForDay, jobCardsForDays } from '../lib/dispatch-phase-cards';
import type { CardPhase, JobCardDragData, PhaseCard } from '../lib/dispatch-phase-cards';
import { usePhaseClockMutation } from '../hooks/usePhaseClockMutation';
import { phaseIsResumable, useResumePhaseMutation } from '../hooks/useResumePhaseMutation';
import { useCancelJobMutation } from '../hooks/useCancelJobMutation';
import { useCrewWriteMutation } from '../hooks/useCrewWriteMutation';
import { dispatchJobsQueryKey, fetchDispatchJobs } from '../hooks/dispatchJobsQuery';
import { CrewPhaseDialog } from '../components/dispatch/CrewPhaseDialog';
import { hasWashDate } from '../lib/job-phase-dates';
import ClearPhaseStartConfirm from '../components/jobs/ClearPhaseStartConfirm';
import { canClearPhaseStart } from '../lib/roles';
import {
  crewForCard,
  describeCrewPhase,
  planDragOffCard,
  planDropOnCard,
  type CrewChipDragData,
  type CrewPlan,
  type JobCardDropData,
} from '../lib/dispatch-crew-phase';
import { jobPositionForCard } from '../lib/job-crew-positions';
import { useLocale } from '../contexts/LocaleContext';
import { formatInstant } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
import {
  crewSpecialtyBadge,
  crewSpecialtyLabel,
  crewSpecialtyRank,
  crewSpecialtyTint,
} from '../lib/crew-specialty';
import { PageHeaderBand } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import EquipmentOutStrip, { EQUIPMENT_OUT_QUERY_KEY } from '../components/dispatch/EquipmentOutStrip';
import { equipmentLabel } from '../lib/equipment-label';
import { equipmentStatusLabel } from '../lib/equipment-status';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
  type LoadFailureKind,
} from '../lib/load-failure';
import { displayValue } from '../features/fieldiq-handoff/contracts';

/**
 * The board's payload shape now lives in `types/dispatch.ts`, exported, and the
 * three DispatchScreen suites annotate their fixtures with it. Before that move
 * these interfaces were unexported and nothing checked them against anything:
 * re-adding the long-dropped `date_time_run` as a required field, and reading it
 * from this component, compiled clean. See the docblock in that file.
 *
 * `CrewMember` is aliased because `types/index.ts` already exports an unrelated
 * legacy `CrewMember` (the mock-data shape: `name`, `worked`, `h2s`); keeping the
 * short name leaves every use site below untouched. `DispatchEquipmentItem` is
 * not imported at all — its only use site was inside `DispatchJob`, which moved.
 *
 * `FilterData` and `TorqueSheetCard` moved for the same reason and are the same
 * defect twice more. They were reached through `as` assertions on an untyped
 * `r.data.data`, and an `as` assertion typechecks against anything — it replaces
 * what the compiler knew rather than checking it. A required phantom field on
 * `TorqueSheetCard`, read by this component, compiled clean on main; so did the
 * same probe one level down inside either `crew[]`. The assertions are gone: the
 * response generics on `api.dispatch.filters` / `.torqueSheets` carry the types
 * now, and the annotated fixtures are what actually enforce them.
 */
import type {
  DispatchJob,
  DispatchCrewMember as CrewMember,
  FilterData,
  TorqueSheetCard,
} from '../types/dispatch';

type ViewMode = 'board' | 'calendar';

// Custom collision detection: job drags → only columns, crew drags → only job cards
const typeAwareCollision: CollisionDetection = (args) => {
  const activeType = args.active.data.current?.type;
  if (activeType === 'crew') {
    // Crew chips can land on another job card OR back on the roster (unassign)
    const crewTargets = args.droppableContainers.filter(
      c => String(c.id).startsWith('jobcard-') || String(c.id) === 'roster-dropzone'
    );
    return rectIntersection({ ...args, droppableContainers: crewTargets });
  }
  if (activeType === 'roster') {
    const jobcardEntries = args.droppableContainers.filter(c => String(c.id).startsWith('jobcard-'));
    return rectIntersection({ ...args, droppableContainers: jobcardEntries });
  }
  // Default: job drag → only columns
  const columnEntries = args.droppableContainers.filter(c => String(c.id).startsWith('col-'));
  return rectIntersection({ ...args, droppableContainers: columnEntries });
};
type CardType = CardPhase;
type TimeView = 'week' | 'month' | 'status' | 'pdf';

/**
 * VIEW STATE LIVES IN THE URL, not in `useState`.
 *
 * The bug: `viewMode` was component state, so navigating away from Dispatch and
 * back unmounted the component and reset it. A dispatcher who was on the
 * Calendar always came back to the Kanban board, and the same went for the
 * Week / Month / Status / Needs PDF tab.
 *
 * A QUERY PARAM RATHER THAN `localStorage`, which is available here and is the
 * wrong shape: "which view am I looking at" is a fact about a URL, not a
 * preference about a device. The param survives a refresh AND back/forward,
 * makes the view bookmarkable and linkable, and lets two tabs sit on two
 * different views.
 *
 * TWO PARAMS, NOT ONE, because these are independent axes. Collapsing them into
 * something like `?view=board.week` would invent a mini-format nothing else in
 * the repo parses.
 *
 * DERIVED EACH RENDER, NEVER MIRRORED INTO STATE. This is the shape
 * screens/trucking/TrucksScreen.tsx:20-23 already uses, followed verbatim rather
 * than invented: read the param, coerce it with a ternary whose fallback IS the
 * default. There is no `useState` to fall out of sync with the URL, and an
 * unrecognised value (`?view=banana`) lands on the default structurally rather
 * than via a guard somebody can forget — it can never render nothing.
 *
 * WHAT DELIBERATELY DOES NOT PERSIST, and why each one would be a bug:
 *   - `currentDate` — coming back to Dispatch should land on TODAY, not on
 *     whichever month you were browsing three days ago. It is also a `Date`
 *     object, and serialising a calendar day through a URL is the exact shape of
 *     the bug class CLAUDE.md catalogues; there is nothing to gain here worth
 *     opening that door.
 *   - `searchQuery`, `customerFilter`, `crewFilter`, `statusFilter` — a filter
 *     that survives navigation is the "why is this list empty" report. The empty
 *     default is honest. (screens/TeamScreen.tsx persists its filters in
 *     `sessionStorage`; that precedent is deliberately not followed.)
 *   - `expandedDays` — tied to a specific month you are no longer on.
 */
const VIEW_PARAM = 'view';
const TAB_PARAM = 'tab';
const DEFAULT_VIEW: ViewMode = 'board';
const DEFAULT_TAB: TimeView = 'week';

const readViewMode = (raw: string | null): ViewMode =>
  raw === 'calendar' ? 'calendar' : DEFAULT_VIEW;

const readTimeView = (raw: string | null): TimeView =>
  raw === 'month' || raw === 'status' || raw === 'pdf' ? raw : DEFAULT_TAB;

/**
 * How many job pills a month-calendar day cell shows before it collapses the
 * rest behind a "+N more" toggle.
 *
 * WHY 3 AND NOT MORE. Measured against a real render: the cell is
 * `min-h-[100px]` with `p-1.5` and a 1px bottom border, so the pill stack gets
 * 87 - 20 (the date number plus its `mb-1`) = 67px before the cell grows. A
 * pill is 23.33px and `space-y-1` adds 4px, so only TWO fit inside the minimum
 * — three already grows the row to ~131px.
 *
 * The cap is not a clipping constraint (the cell's `overflow` computes to
 * `visible`); it is a deliberate ceiling on row height. These are CSS grid rows,
 * so a cell that grows stretches all seven cells in its week. Raising the cap to
 * 5 would put every week that contains one busy Wednesday at ~186px whether or
 * not the other six days have any work. Expanding on demand pays that cost only
 * on the day someone actually asked about, which is why this stayed at 3 when
 * the "+N more" dead end was fixed.
 */
const CALENDAR_PILL_CAP = 3;

const DispatchScreen: React.FC = () => {
  const navigate = useNavigate();
  const { company } = useAuth();
  const { config } = useIndustry();
  // See the block above VIEW_PARAM: derived from the URL, never mirrored into
  // state, so the view survives leaving Dispatch and coming back.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = readViewMode(searchParams.get(VIEW_PARAM));
  const timeView = readTimeView(searchParams.get(TAB_PARAM));

  // Writes go through one helper so the two params cannot drift apart, and so
  // the default state is a CLEAN URL — `/dispatch`, not `/dispatch?view=board`.
  // `replace` rather than push: flipping a view is not a navigation step, and
  // pushing would make Back walk the user through every tab they tried instead
  // of leaving the screen.
  const setView = (next: { view?: ViewMode; tab?: TimeView }) => {
    const params = new URLSearchParams(searchParams);
    if (next.view !== undefined) {
      if (next.view === DEFAULT_VIEW) params.delete(VIEW_PARAM);
      else params.set(VIEW_PARAM, next.view);
    }
    if (next.tab !== undefined) {
      if (next.tab === DEFAULT_TAB) params.delete(TAB_PARAM);
      else params.set(TAB_PARAM, next.tab);
    }
    setSearchParams(params, { replace: true });
  };
  const setViewMode = (view: ViewMode) => setView({ view });
  const setTimeView = (tab: TimeView) => setView({ tab });

  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [crewFilter, setCrewFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Calendar day cells that the user has expanded past CALENDAR_PILL_CAP, keyed
  // on `yyyy-MM-dd`. A Set rather than a single day: expanding Tuesday should
  // not silently collapse Wednesday, because comparing two busy days against
  // each other is the reason you expand one in the first place.
  //
  // Keyed on the date STRING, not the Date object — a `Date` is compared by
  // identity in a Set, so `new Date(same day)` would never match and the cell
  // could never be collapsed again.
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const toggleDayExpanded = (dayKey: string) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayKey)) next.delete(dayKey); else next.add(dayKey);
      return next;
    });
  };

  // Build query params for API call
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (searchQuery) params.search = searchQuery;
    if (customerFilter) params.customer_id = customerFilter;
    if (crewFilter) params.crew_id = crewFilter;
    if (statusFilter) params.status = statusFilter;
    return params;
  }, [searchQuery, customerFilter, crewFilter, statusFilter]);

  // Fetch dispatch jobs
  // `tenant_today` rides beside the list and is the board's only "today" for
  // phase carry-over — see lib/dispatch-phase-cards.ts. Absent reads as null.
  /**
   * THE BOARD HAS THREE STATES, AND IT USED TO HAVE TWO.
   *
   * `isError` alone is not the failure branch. With React Query's default
   * `networkMode: 'online'` a query that fails and wants to retry sits in
   * PAUSED — `error` null, `data` undefined, `isLoading` false — so every
   * branch keyed on `isError` says "fine" and `jobsData?.jobs ?? []` hands the
   * board an empty array. A dispatcher in a dead zone would have been shown a
   * clean week with no jobs on it, which on THIS screen means "nothing is
   * scheduled". `LOAD_FAILURE_QUERY_OPTIONS` stops a refusal being retried and
   * `queryLoadFailure` reads the paused state as well as the settled one.
   */
  const jobsQuery = useQuery({
    // Shared with the Overview — one key, one shape. See hooks/dispatchJobsQuery.ts.
    queryKey: dispatchJobsQueryKey(queryParams),
    queryFn: () => fetchDispatchJobs(queryParams),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: jobsData, isLoading } = jobsQuery;
  const jobsFailure = queryLoadFailure(jobsQuery);
  const { locale } = useLocale();
  const phaseClockMutation = usePhaseClockMutation();

  // Fetch filter options
  const { data: filtersData } = useQuery({
    queryKey: ['dispatch-filters'],
    queryFn: () => api.dispatch.filters().then(r => r.data.data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  // Fetch assignable roster (crew pool for the pinned CREW column)
  const rosterQuery = useQuery({
    queryKey: ['dispatch-roster'],
    queryFn: () => api.users.list().then(r => {
      const raw: any[] = r.data?.data ?? r.data ?? [];
      return raw.map((u: any) => ({
        id: u.id,
        first_name: u.first_name ?? u.firstName,
        last_name: u.last_name ?? u.lastName,
        initials: u.initials,
        specialty: u.specialty,
        // The PRIMARY position, by the tenant's name — what the CREW column groups on.
        position_name: u.position_name ?? u.positionName ?? null,
        role: u.role,
        employment_status: u.employment_status ?? u.employmentStatus,
      }));
    }),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: rosterData } = rosterQuery;
  const rosterFailure = queryLoadFailure(rosterQuery);

  // Fetch torque sheet queue (always, for badge count)
  const queryClient = useQueryClient();
  const torqueQuery = useQuery({
    queryKey: ['torque-sheets'],
    queryFn: () => api.dispatch.torqueSheets().then(r => r.data.data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const { data: torqueSheetsData } = torqueQuery;
  const torqueFailure = queryLoadFailure(torqueQuery);

  const torqueSheets: TorqueSheetCard[] = torqueSheetsData ?? [];

  const clearTorqueSheet = useMutation({
    mutationFn: (id: string) => api.dispatch.clearTorqueSheet(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['torque-sheets'] });
    },
  });

  /* Memoised because `unstartedJobs` below depends on it: a fresh `[]` every
     render would recompute that filter on every render. The `?? []` collapse is
     safe only because the failure branch renders INSTEAD of the board — see the
     docblock on the query above. */
  const jobs: DispatchJob[] = useMemo(() => jobsData?.jobs ?? [], [jobsData]);
  const boardContext = { tenantToday: jobsData?.tenantToday ?? null, timeZone: locale.time_zone };
  const filters: FilterData = filtersData ?? { customers: [], crew: [], statuses: [] };

  /**
   * HAS THE DATA ARRIVED — not `!isLoading`.
   *
   * The two previous re-skins (fe#729, fe#730) both shipped a first draft that
   * gated the band's stats on `isLoading`, which is false in the paused state
   * as well as the loaded one. The band then printed four confident zeros
   * directly above a notice saying the list could not be loaded. A figure is
   * claimed only when the query that feeds it actually answered.
   */
  const jobsArrived = !isLoading && !jobsFailure && jobsData !== undefined;
  const rosterArrived = !rosterFailure && rosterData !== undefined;
  const torqueArrived = !torqueFailure && torqueSheetsData !== undefined;

  // Assignable crew — field workers + supervisors who are still active
  const assignableRoster = useMemo(() => {
    return (rosterData ?? [])
      .filter((u: any) =>
        ['field_worker', 'supervisor'].includes(u.role) &&
        (u.employment_status === 'active' || u.employment_status == null)
      )
      .sort((a: any, b: any) =>
        (a.last_name ?? '').localeCompare(b.last_name ?? '') ||
        (a.first_name ?? '').localeCompare(b.first_name ?? '')
      );
  }, [rosterData]);

  // Group roster by PRIMARY position for the CREW column subheadings — the
  // tenant's own position names, alphabetical, hands with none last. It was
  // the four-value specialty enum, which says nothing about a tenant whose
  // positions are not those four.
  const rosterByPosition = useMemo(() => {
    const groups = new Map<string, any[]>();
    for (const u of assignableRoster) {
      const key = (u.position_name && String(u.position_name).trim()) || UNSPECIFIED_KEY;
      const arr = groups.get(key) || [];
      arr.push(u);
      groups.set(key, arr);
    }
    return Array.from(groups.entries()).sort((a, b) =>
      Number(a[0] === UNSPECIFIED_KEY) - Number(b[0] === UNSPECIFIED_KEY) || a[0].localeCompare(b[0])
    );
  }, [assignableRoster]);

  // Parse scheduled_date strings into Dates for grouping.
  // scheduled_date is a CALENDAR DAY, not an instant — the day the crew is
  // expected on site. Time-of-day for the thread wash lives in date_time_wash;
  // there is no sibling run column any more — scheduled_date IS the run date
  // (API 0340).
  // Currently uncalled; parseLocalDate below and lib/dispatch-phase-cards.ts are what the board
  // reads. Left in place, and correct, so a future caller does not reintroduce
  // the UTC-midnight shift by copying it.
  const getJobDate = (job: DispatchJob): Date | null => parseCalendarDate(job.scheduled_date);

  // Parse date value (string or Date object) as local date to avoid timezone shift
  // Handles: "2026-03-17T00:00:00Z", "2026-03-17 12:00:00", "2026-03-17", Date objects
  const parseLocalDate = (dateVal: any): Date | null => {
    if (!dateVal) return null;
    if (dateVal instanceof Date) {
      return isNaN(dateVal.getTime()) ? null : new Date(dateVal.getFullYear(), dateVal.getMonth(), dateVal.getDate());
    }
    const str = String(dateVal);
    // Split on T or space to get the date part
    const datePart = str.split(/[T ]/)[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!y || !m || !d) return null;
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
  };

  // Cards for a day column: one per phase per job, carried across every day a
  // started phase is still running. The rules live in lib/dispatch-phase-cards.ts.
  const getCardsForDay = (day: Date): PhaseCard[] =>
    cardsForDay(jobs, format(day, 'yyyy-MM-dd'), boardContext);

  // Month calendar cell: one card per job, carried like the week board.
  const getJobCardsForDay = (day: Date): PhaseCard[] =>
    jobCardsForDays(jobs, [format(day, 'yyyy-MM-dd')], boardContext);

  /**
   * Jobs whose RUN day has arrived or passed and which nobody has started.
   *
   * HOISTED OUT OF THE BANNER'S IIFE so the band's stat and the banner are one
   * definition rather than two. The band is not allowed to answer a question
   * the body already answers differently — that is the same "two answers to one
   * question" the no-client-recomputation rule is about, one scope down.
   *
   * `parseCalendarDate` and NOT `new Date(...)`: scheduled_date is a calendar
   * day, and `new Date('2026-03-23')` is UTC midnight, which is 18:00 the
   * previous day in Alberta — so TOMORROW's job compared as <= today and the
   * banner told the dispatcher it was already overdue.
   *
   * "Today" is the API's `tenant_today`, the same day carry-over ends on, and
   * NOT the browser's clock. After 18:00 in Alberta, or for a dispatcher
   * viewing a tenant in another zone, the browser's day is a different day,
   * and the screen held two todays. Both sides are 'YYYY-MM-DD', so the
   * string comparison is a day comparison.
   *
   * NULL WHEN `tenant_today` IS ABSENT, not `[]`. Without the tenant's day the
   * list is unknown, which is not "nobody is late": the banner shows nothing
   * and the band's stat reads an em dash. No browser fallback — that would be
   * the second today again.
   */
  const tenantToday = jobsData?.tenantToday ?? null;
  const unstartedJobs = useMemo((): DispatchJob[] | null => {
    if (!tenantToday) return null;
    return jobs.filter((j) => {
      if (j.status !== 'pending' && j.status !== 'scheduled') return false;
      const schedDay = calendarDayString(j.scheduled_date);
      return schedDay !== null && schedDay <= tenantToday;
    });
  }, [jobs, tenantToday]);

  // Get jobs for a specific status (Status view)
  const getJobsForStatus = (status: string) => {
    return jobs.filter(job => job.status === status);
  };

  // Month tab week column: one card per job across the week's seven days.
  const getJobCardsForWeek = (weekStart: Date): PhaseCard[] =>
    jobCardsForDays(jobs, Array.from({ length: 7 }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd')), boardContext);

  // The board card's status colour used to be routed through a local
  // `getStatusColor` helper here, feeding an 8px dot. Both are gone: the dot
  // became the card's 4px left stripe, and `JobCard` reads `jobStatusStripe`
  // from src/lib/job-status.ts directly rather than having a colour threaded
  // through it as a prop. One fewer copy of a decision that file exists to hold
  // once — the calendar pill reads the same palette from the same place.

  // 1 day trailing + today + 5 days ahead = 7 days, chronological
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    addDays(currentDate, i - 1)
  );

  // Month view columns (weeks)
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const monthWeeks: Date[] = [];
  let weekCursor = startOfWeek(monthStart, { weekStartsOn: 0 });
  while (weekCursor <= monthEnd) {
    monthWeeks.push(weekCursor);
    weekCursor = addWeeks(weekCursor, 1);
  }

  // Status view columns
  const statusColumns = ['pending', 'scheduled', 'active', 'on_hold', 'completed'];

  const formatDayHeader = (day: Date) => {
    return {
      dayName: format(day, 'EEE').toUpperCase(),
      dateStr: format(day, 'MMM d')
    };
  };

  const navigateWeek = (direction: number) => {
    setCurrentDate(addWeeks(currentDate, direction));
  };

  const statusLabels: Record<string, string> = {
    'pending': 'PENDING',
    'scheduled': 'SCHEDULED',
    'active': 'ACTIVE',
    'on_hold': 'ON HOLD',
    'completed': 'COMPLETED'
  };

  // Drag-and-drop
  const [activeJob, setActiveJob] = useState<DispatchJob | null>(null);
  // The dragged card's run/wash type. Carried so the DragOverlay can render the
  // SAME card the column does — see the DragOverlay block below.
  const [activeCardType, setActiveCardType] = useState<CardType | undefined>(undefined);
  const [activeDragType, setActiveDragType] = useState<'job' | 'crew' | 'roster' | null>(null);
  const [activeCrewMember, setActiveCrewMember] = useState<CrewMember | null>(null);
  const crewWrite = useCrewWriteMutation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const rescheduleJob = useMutation({
    mutationFn: (vars: { jobId: string; data: Record<string, any> }) =>
      api.dispatch.updateJob(vars.jobId, vars.data),
    onMutate: async (vars) => {
      // Optimistic update — move card instantly
      await queryClient.cancelQueries({ queryKey: ['dispatch-jobs', queryParams] });
      const prev = queryClient.getQueryData(['dispatch-jobs', queryParams]);
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      queryClient.setQueryData(['dispatch-jobs', queryParams], (old: any) => {
        if (!Array.isArray(old?.jobs)) return old;
        return {
          ...old,
          jobs: old.jobs.map((j: any) =>
            j.id === vars.jobId ? { ...j, ...vars.data } : j
          ),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      // Revert on failure
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      if (context?.prev) queryClient.setQueryData(['dispatch-jobs', queryParams], context.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-cert-summary'] });
      queryClient.invalidateQueries({ queryKey: ['home-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-report'] });
      queryClient.invalidateQueries({ queryKey: EQUIPMENT_OUT_QUERY_KEY });
    },
  });

  function handleDragStart(event: DragStartEvent) {
    const data = event.active.data.current;
    if (data?.type === 'crew') {
      setActiveDragType('crew');
      setActiveCrewMember(data.crewMember ?? null);
      setActiveJob(null);
      setActiveCardType(undefined);
    } else if (data?.type === 'roster') {
      setActiveDragType('roster');
      setActiveCrewMember(data.crewMember ?? null);
      setActiveJob(null);
      setActiveCardType(undefined);
    } else {
      setActiveDragType('job');
      setActiveJob(data?.job ?? null);
      setActiveCardType(data?.cardType);
      setActiveCrewMember(null);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveJob(null);
    setActiveCardType(undefined);
    setActiveDragType(null);
    setActiveCrewMember(null);
    const { active, over } = event;
    if (!over) return;

    // ── Crew chip → roster column (unassign) ──
    // Off a card: a 'both' or unstated hand keeps the other phase, anyone else
    // leaves the job — lib/dispatch-crew-phase.ts.
    if (active.data.current?.type === 'crew' && over.id === 'roster-dropzone') {
      const chip = active.data.current as CrewChipDragData;
      const member = crewMemberOn(chip.sourceJobId, chip.userId) ?? chip.crewMember;
      const displayName = [member?.first_name, member?.last_name]
        .filter(n => n && n.trim()).join(' ') || 'Crew member';
      const plan = planDragOffCard({ member, source: { cardType: chip.sourceCardType ?? 'run', split: chip.sourceSplit } });
      if (plan.kind === 'none') return;
      crewWrite.mutateAsync({ jobId: chip.sourceJobId, body: plan.body })
        .then(() => {
          toast.success(plan.kind === 'set-phase'
            ? `${displayName} is now ${describeCrewPhase(plan.phase)}`
            : `${displayName} removed from job`);
        })
        .catch(() => {
          toast.error(`Failed to remove ${displayName} from job.`);
        });
      return;
    }

    // ── Roster chip → job card (assign) ──
    if (active.data.current?.type === 'roster') {
      const { userId, crewMember } = active.data.current as {
        userId: string; crewMember: CrewMember;
      };
      const drop = over.data.current as JobCardDropData | undefined;
      const targetJobId = drop?.jobId;
      if (!targetJobId) return;

      const displayName = [crewMember?.first_name, crewMember?.last_name]
        .filter(n => n && n.trim()).join(' ') || 'Crew member';

      // Planned against the unfiltered crew array: a wash-only hand is not on
      // the run card and is still on the job.
      const targetJob = jobs.find(j => j.id === targetJobId);
      const plan = planDropOnCard({
        userId,
        targetCrew: targetJob?.crew ?? [],
        target: { jobId: targetJobId, cardType: drop.cardType ?? 'run', split: drop.split === true },
        fromSameCard: false,
      });
      if (plan.kind === 'none') {
        toast.error(`${displayName} is already on ${targetJob?.job_number || targetJobId}`);
        return;
      }

      crewWrite.mutateAsync({ jobId: targetJobId, body: plan.body, known: [rosterCrewMember(crewMember)] })
        .then(() => {
          if (plan.kind === 'set-phase') toast.success(`${displayName} is now ${describeCrewPhase(plan.phase)}`);
          else checkCrewConflict(userId, displayName, targetJobId, null);
        })
        .catch(() => {
          toast.error(`Failed to add ${displayName} to ${targetJob?.job_number || targetJobId}.`);
        });
      return;
    }

    // ── Crew drag path ──
    if (active.data.current?.type === 'crew') {
      const chip = active.data.current as CrewChipDragData;
      const { userId, sourceJobId } = chip;
      const drop = over.data.current as JobCardDropData | undefined;
      const targetJobId = drop?.jobId;
      if (!targetJobId) return;

      const member = crewMemberOn(sourceJobId, userId) ?? chip.crewMember;
      const displayName = [member?.first_name, member?.last_name]
        .filter(n => n && n.trim()).join(' ') || 'Crew member';
      const targetJob = jobs.find(j => j.id === targetJobId);
      const targetCardType = drop.cardType ?? 'run';
      const plan = planDropOnCard({
        userId,
        targetCrew: targetJob?.crew ?? [],
        target: { jobId: targetJobId, cardType: targetCardType, split: drop.split === true },
        fromSameCard: targetJobId === sourceJobId && targetCardType === (chip.sourceCardType ?? 'run'),
      });

      // Same job, other card: only a phase can change — the hand stays where
      // they were and gains (or narrows to) this card's phase.
      if (targetJobId === sourceJobId) {
        if (plan.kind !== 'set-phase') return;
        crewWrite.mutateAsync({ jobId: targetJobId, body: plan.body })
          .then(() => toast.success(`${displayName} is now ${describeCrewPhase(plan.phase)}`))
          .catch(() => toast.error(`Failed to update ${displayName} on ${targetJob?.job_number || targetJobId}.`));
        return;
      }

      if (plan.kind === 'none') {
        toast.error(`${displayName} is already on ${targetJob?.job_number || targetJobId}`);
        return;
      }

      // Onto the target first, then off the source card by the same rule as a
      // drop on the CREW column — a 'both' hand keeps the source's other phase.
      const offPlan: CrewPlan = planDragOffCard({
        member,
        source: { cardType: chip.sourceCardType ?? 'run', split: chip.sourceSplit },
      });
      crewWrite.mutateAsync({ jobId: targetJobId, body: plan.body, known: [member] })
        .then(() => {
          if (offPlan.kind === 'none') return;
          return crewWrite.mutateAsync({ jobId: sourceJobId, body: offPlan.body })
            .catch(() => {
              const sourceJob = jobs.find(j => j.id === sourceJobId);
              toast.error(
                `Added to ${targetJob?.job_number || targetJobId} but could not remove from ${sourceJob?.job_number || sourceJobId} — please check both jobs.`
              );
            });
        })
        .then(() => {
          // Task 4: conflict warning (check before invalidation uses stale cache)
          checkCrewConflict(userId, displayName, targetJobId, offPlan.kind === 'remove' ? sourceJobId : null);
        })
        .catch(() => {
          toast.error(`Failed to add ${displayName} — move aborted.`);
        });
      return;
    }

    // ── Job drag path ──
    const colId = over.id as string;

    // Custom collision only returns columns (col-*), so no fallback needed
    if (!String(colId).startsWith('col-')) return;

    // The card says what it is in its drag data. The id is an opaque key and is
    // never parsed — it carries the day now, and a suffix test on it is how a
    // wash card would silently start moving scheduled_date.
    const dragData = active.data.current as JobCardDragData | undefined;
    if (!dragData?.jobId) return;
    const { jobId } = dragData;
    const isWashCard = dragData.cardType === 'wash';

    if (timeView === 'week') {
      if (colId === 'col-pending') {
        console.log('[dispatch] → pending:', jobId);
        rescheduleJob.mutate({ jobId, data: { scheduled_date: null, status: 'pending' } });
      } else if (colId.startsWith('col-day-')) {
        const newDate = colId.replace('col-day-', '');
        const job = jobs.find(j => j.id === jobId);
        const dateField = isWashCard ? 'date_time_wash' : 'scheduled_date';
        // Send as timestamp to avoid type issues with timestamptz column.
        // The DAY moves; the operator's time-of-day does not. This line used to
        // send a hardcoded T12:00:00, so every drag silently overwrote a typed
        // rig-up hour with noon — and 12:00 is still the most common value in
        // the column estate-wide (27 of 150, 18%) because of it. There is no
        // jobs audit table, so those are unrecoverable; this stops the next one.
        // Noon survives only as the no-date case: a pending card dragged onto a
        // day for the first time. An existing UTC midnight is a real reading —
        // "no time entered" — and is carried across as-is rather than promoted
        // to a noon nobody typed. See scheduledTimeOfDay for why the literal
        // UTC clock is the operator's clock.
        const data: Record<string, any> = {
          [dateField]: `${newDate}T${scheduledTimeOfDay(job?.[dateField]) ?? '12:00:00'}`,
        };
        if (job?.status === 'pending') {
          data.status = 'scheduled';
        }
        console.log('[dispatch] → move:', jobId, dateField, newDate, data);
        rescheduleJob.mutate({ jobId, data });
      }
    } else if (timeView === 'status') {
      const newStatus = colId.replace('col-status-', '');
      rescheduleJob.mutate({ jobId, data: { status: newStatus } });
    }
  }

  // The board's own row for a hand — the drag payload is a render-time copy.
  function crewMemberOn(jobId: string, userId: string): CrewMember | undefined {
    return jobs.find(j => j.id === jobId)?.crew?.find(c => c.id === userId);
  }

  // Task 4: After crew move, check for same-date double-booking
  // Simulates the post-move state using the stale jobs array
  function checkCrewConflict(userId: string, displayName: string, targetJobId: string, sourceJobId: string | null) {
    // Build post-move view: person is on target, removed from source
    const jobsWithUser = jobs.filter(j => {
      if (sourceJobId && j.id === sourceJobId) return false; // removed from source
      if (j.id === targetJobId) return true;  // added to target
      return j.crew?.some(c => c.id === userId);
    });
    // Group by scheduled_date
    const dateMap = new Map<string, DispatchJob[]>();
    for (const j of jobsWithUser) {
      const d = j.scheduled_date ? parseLocalDate(j.scheduled_date) : null;
      if (!d) continue;
      const key = format(d, 'yyyy-MM-dd');
      const arr = dateMap.get(key) || [];
      arr.push(j);
      dateMap.set(key, arr);
    }
    dateMap.forEach((dateJobs) => {
      if (dateJobs.length > 1) {
        const dateLabel = format(parseLocalDate(dateJobs[0].scheduled_date)!, 'MMM d');
        const jobNumbers = dateJobs.map((j: DispatchJob) => j.job_number).join(', ');
        toast.warning(
          `${displayName} is now on ${dateJobs.length} jobs ${dateLabel} — ${jobNumbers}`
        );
      }
    });
  }

  return (
    /* The plain white text utility stays on the root deliberately (named
       nowhere in this comment — the Tailwind scanner reads prose as source).
       theme.css rewrites that exact selector to the heading ink under the light
       attribute, so inherited ink on this page is already theme-correct;
       swapping it for a body-ink token would repaint every descendant that
       inherits it. */
    <div data-testid="dispatch-page" className="animate-fade-in min-h-full bg-fieldiq-dark text-white">
      {/* The app shell renders the one global branded bar. Pages never do. */}
      <PageHeaderBand
        eyebrow="Casing operations"
        title="Dispatch"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <button
            type="button"
            onClick={() => navigate('/jobs/new')}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            New Job
          </button>
        }
        /* FOUR FIGURES, EACH FROM A QUERY THIS SCREEN ALREADY RUNS — no fifth
           request, and nothing here is recomputed from a figure the API states.
           Jobs and Unstarted come off the board list, Crew off the roster,
           Needs PDF off the torque-sheet queue, and each is gated on ITS OWN
           query having answered rather than on one shared flag.

           THE THIRD LABEL IS FOUR CHARACTERS ON PURPOSE. The hero circle's edge
           crosses the third stat column between 1024 and 1048 wide and the
           labels swept there run to about 70px (fe#698). "Crew available" would
           not have fitted and would have needed the sweep re-run; "Crew" with
           the sub-line doing the explaining does not.

           NEEDS PDF IS A STAT AND NO LONGER A BADGE ON ITS TAB. It used to be a
           red count pinned to the "Needs PDF" tab, and the standard says a tab
           carries no count — a tab switches which question the page answers, so
           a number on it counts a dataset the reader is not looking at. Moving
           it here does not weaken the signal: the band is on screen in every
           view, and the tab's badge was not.

           A FIGURE IS A DASH WHEN ITS QUERY FAILED, and a pulse only while one
           is genuinely in flight. A permanent pulse over a failed load is the
           same lie as a zero, drawn more slowly. */
        stats={[
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            label: 'Jobs',
            sub: 'Matching filters',
            tone: 'info',
            loading: isLoading && !jobsFailure,
            value: displayValue(jobsArrived ? jobs.length : null),
          },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Unstarted',
            sub: 'Due today or past',
            tone: 'accent',
            loading: isLoading && !jobsFailure,
            value: displayValue(jobsArrived && unstartedJobs ? unstartedJobs.length : null),
          },
          {
            icon: <Users className="size-4" aria-hidden="true" />,
            label: 'Crew',
            sub: 'Assignable',
            tone: 'success',
            loading: !rosterArrived && !rosterFailure,
            value: displayValue(rosterArrived ? assignableRoster.length : null),
          },
          {
            icon: <FileText className="size-4" aria-hidden="true" />,
            label: 'Needs PDF',
            sub: 'Torque sheets',
            tone: 'neutral',
            loading: !torqueArrived && !torqueFailure,
            value: displayValue(torqueArrived ? torqueSheets.length : null),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* TWO TAB LEVELS, BOTH IN THE BODY, NEVER ON THE BAND.
              Everything drawn on `--fi-brand` has to be spelled in constant
              white opacities, so a strip up there would need its own
              permanently-navy treatment unlike every other strip in the app —
              and the circle's clearance was swept against stat text only.

              BOTH LEVELS ARE TABS AND NOT CHIPS, by fe#704's test: a chip
              narrows ONE list and every chip asks the same question of the same
              rows; a tab switches WHICH QUESTION the page answers. Kanban and
              Calendar share no list and no controls. Week, Month, Status and
              Needs PDF share even less — Needs PDF is a different table off a
              different endpoint. The search box and the three selects below ARE
              chips by that test and stay where they are: they narrow the one
              job list, and every view reads it. */}
          {/* Every asset not in the yard, company-wide, above both views: the
              board's job cards only show gear on jobs inside the window on
              screen, and dispatch must never have to hunt for it. */}
          <EquipmentOutStrip />

          <ViewStrip view={viewMode} onChange={setViewMode} />

          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* ONLY THE INNER STRIP IS GATED. `timeView` is read in exactly one
                place, under `viewMode === 'board'`; the strip used to render
                unconditionally, so in Calendar all four tabs drew, highlighted
                on click and did nothing. The date nav beside it stays in both
                views — the calendar's own header has month arrows but no Today,
                so gating the whole row would take Today away from calendar
                users, which is a regression rather than a fix. */}
            {viewMode === 'board'
              ? <BoardTabs tab={timeView} onChange={setTimeView} />
              : <span />}
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous week"
                onClick={() => navigateWeek(-1)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-2 text-fieldiq-muted transition hover:text-fieldiq-text"
              >
                <ChevronLeft className="size-5" aria-hidden="true" />
              </button>
              {/* THE CARD SURFACE UNDER IT IS LOAD-BEARING, not chrome to match
                  the arrows. `--fieldiq-accent-signal` is #b45309 in light,
                  which is 5.02 on `--fieldiq-card` and **4.36 on
                  `--fieldiq-dark`** — under the floor on the page surface this
                  row sits on. MEASURED in the browser, not reasoned: the first
                  draft had no background here and read 4.36. Giving it the same
                  fill its two neighbours carry buys the contrast back and makes
                  it read as their peer, which it is. */}
              <button
                type="button"
                onClick={() => setCurrentDate(new Date())}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-extrabold text-fieldiq-accent-signal transition hover:border-fieldiq-accent"
              >
                Today
              </button>
              <button
                type="button"
                aria-label="Next week"
                onClick={() => navigateWeek(1)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card p-2 text-fieldiq-muted transition hover:text-fieldiq-text"
              >
                <ChevronRight className="size-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* SEARCH & FILTERS — these narrow the one job list, so they sit with
              the board rather than on the band. */}
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="text"
                aria-label="Find job, customer, well or crew"
                placeholder="Find job, customer, well, crew..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card py-3 pl-12 pr-4 text-fieldiq-text placeholder-fieldiq-muted focus:border-fieldiq-accent focus:outline-none"
              />
            </div>

            <div className="flex flex-wrap gap-3">
              <select
                aria-label="Filter by customer"
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-2 text-sm text-fieldiq-text"
              >
                <option value="">All Customers</option>
                {filters.customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <select
                aria-label="Filter by crew"
                value={crewFilter}
                onChange={(e) => setCrewFilter(e.target.value)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-2 text-sm text-fieldiq-text"
              >
                <option value="">All Crew</option>
                {filters.crew.map(c => (
                  <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
                ))}
              </select>
              <select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-4 py-2 text-sm text-fieldiq-text"
              >
                <option value="">All Status</option>
                {filters.statuses.map(s => (
                  <option key={s} value={s}>{statusLabels[s] || s.toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Unstarted Jobs Banner (today + overdue) */}
          {unstartedJobs && unstartedJobs.length > 0 && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
              {/* The accent-signal token, not the raw amber-400 ink utility.
                  A raw palette ink keeps its bright dark-mode value on a light
                  panel and measured under the floor there; the token remaps to
                  #b45309 in light and is the same gold in dark. */}
              <h3 className="mb-2 text-sm font-extrabold text-fieldiq-accent-signal">
                {unstartedJobs.length} job{unstartedJobs.length > 1 ? 's' : ''} need{unstartedJobs.length === 1 ? 's' : ''} to be started
              </h3>
              <div className="space-y-2">
                {unstartedJobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between rounded-fi-control bg-fieldiq-dark px-3 py-2">
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/jobs/${j.id}`, { state: { from: '/dispatch' } })}>
                      <p className="truncate text-sm font-bold text-white">
                        {j.customer?.name || 'No Client'}
                      </p>
                      <p className="truncate text-sm font-medium text-fieldiq-text">
                        {j.job_number}{j.rig_name ? ` · ${j.rig_name}` : ''}{j.rig_number ? ` #${j.rig_number}` : ''}{j.job_type ? ` · ${getJobTypeLabel(j.job_type)}` : ''}
                      </p>
                    </div>
                    <button
                      // The banner lists jobs past their RUN day, so its play
                      // button starts the run phase.
                      onClick={(e) => { e.stopPropagation(); phaseClockMutation.mutate({ jobId: j.id, phase: 'run', action: 'start', jobNumber: j.job_number }); }}
                      disabled={phaseClockMutation.isPending}
                      /* A SATURATED FILL WITH `--fi-signal-ink`, not green ink
                         on a green wash. A green-400 ink on a 20% green wash
                         measured 2.53 in light; the signal hues have no light
                         twin by design, so one dark ink holds on them in both
                         themes. */
                      className="ml-2 flex flex-shrink-0 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-green px-3 py-1.5 text-xs font-extrabold text-fi-signal-ink transition hover:brightness-95 disabled:opacity-50"
                    >
                      <Play size={12} aria-hidden="true" /> Start
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* LOADING */}
          {isLoading && !jobsFailure && (
            <div className="flex justify-center py-12">
              <Loader size={32} className="animate-spin text-fieldiq-accent" aria-label="Loading dispatch jobs" />
            </div>
          )}

          {/* THE FAILURE BRANCH, WHERE THE BOARD WOULD HAVE BEEN.
              Not a banner above an empty board: a banner leaves the empty week
              visible underneath it, and on a dispatch board an empty week reads
              as "nothing is scheduled". The board and the calendar are both
              suppressed below while this renders. */}
          {jobsFailure && (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice
                error={queryFailureError(jobsQuery)}
                kind={jobsFailure}
                subject="dispatch jobs"
                allowed="admins, office staff and dispatchers"
              />
            </div>
          )}

        {/* BOARD VIEWS */}
        {!isLoading && !jobsFailure && viewMode === 'board' && (
          <DndContext sensors={sensors} collisionDetection={typeAwareCollision} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="overflow-x-auto">
            {/* WEEK VIEW */}
            {timeView === 'week' && (() => {
              const pendingJobs = jobs.filter(j => j.status === 'pending');
              return (
                <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                  {/* CREW roster column — pinned; drop a crew chip here to unassign */}
                  <RosterColumn
                    rosterByPosition={rosterByPosition}
                    count={assignableRoster.length}
                    failure={rosterFailure}
                  />

                  {/* PENDING column */}
                  <DroppableColumn id="col-pending">
                    <div className="p-4 border-b border-fieldiq-border">
                      <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold text-sm uppercase">PENDING</h3>
                        <span className="bg-fieldiq-border text-fieldiq-muted px-2 py-1 rounded-full text-xs">{pendingJobs.length}</span>
                      </div>
                    </div>
                    <div className="p-3 space-y-3">
                      {pendingJobs.map(job => <DraggableJobCard key={job.id} job={job} isDragOverlay={false} />)}
                      <button
                        onClick={() => navigate('/jobs/new')}
                        className="w-full py-3 border-2 border-dashed border-fieldiq-border rounded-fi-control text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-text transition flex items-center justify-center gap-2"
                      >
                        <Plus className="w-4 h-4" /> Add card
                      </button>
                    </div>
                  </DroppableColumn>

                  {/* Day columns */}
                  {weekDays.map((day, idx) => {
                    const { dayName, dateStr } = formatDayHeader(day);
                    const dayCards = getCardsForDay(day);
                    const colId = `col-day-${format(day, 'yyyy-MM-dd')}`;
                    // The tenant's day, not the browser's: see `unstartedJobs`.
                    // No tenant_today, no highlight.
                    const isToday = tenantToday !== null && format(day, 'yyyy-MM-dd') === tenantToday;
                    return (
                      <DroppableColumn key={idx} id={colId}>
                        <div className={`p-4 border-b ${isToday ? 'border-b-2 border-b-fieldiq-accent border-fieldiq-border' : 'border-fieldiq-border'}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-white font-semibold text-sm uppercase">{dayName}</h3>
                              <p className={`text-xs mt-1 ${isToday ? 'text-fieldiq-accent font-semibold' : 'text-fieldiq-muted'}`}>{dateStr}</p>
                            </div>
                            <span className="bg-fieldiq-border text-fieldiq-muted px-2 py-1 rounded-full text-xs">{dayCards.length}</span>
                          </div>
                        </div>
                        <div className="p-3 space-y-3">
                          {dayCards.map(entry => <DraggableJobCard key={entry.dragId} job={entry.job} isDragOverlay={false} card={entry} />)}
                          <button
                            onClick={() => navigate('/jobs/new', { state: { scheduledDate: format(day, 'yyyy-MM-dd') } })}
                            className="w-full py-3 border-2 border-dashed border-fieldiq-border rounded-fi-control text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-text transition flex items-center justify-center gap-2"
                          >
                            <Plus className="w-4 h-4" /> Add card
                          </button>
                        </div>
                      </DroppableColumn>
                    );
                  })}

                  {/* NEEDS TICKET column — completed jobs with no ticket */}
                  {(() => {
                    const needsTicket = jobs.filter(j => j.status === 'completed' && (j.ticket_count || 0) === 0);
                    return (
                      /* THE PURPLE IS GONE. There are five signal hues — amber,
                         blue, green, red, slate — and no purple, so every purple
                         utility here was a raw palette value with no light twin
                         and no contrast review behind it: the 400-weight purple
                         ink (#c084fc) on a white panel measures 2.2. The column
                         keeps a hue by taking blue, which is the nearest one by
                         meaning (committed, waiting on the office), and says
                         which column it is in the words.

                         THE HUE NOW RIDES ON FILLS, NEVER ON INK. The count is a
                         saturated badge with `--fi-signal-ink` on it — the
                         documented idiom that holds in both themes — and the dot
                         and the borders are fills and edges. */
                      <div className="bg-fieldiq-card rounded-fi-panel min-h-[600px] w-[280px] flex-shrink-0 border border-fieldiq-border">
                        <div className="p-4 border-b border-fieldiq-border">
                          <div className="flex items-center justify-between">
                            <h3 className="text-white font-semibold text-sm uppercase">NEEDS TICKET</h3>
                            <span className="bg-fi-signal-blue text-fi-signal-ink border border-black/20 px-2 py-0.5 rounded-fi-pill text-xs font-extrabold">{needsTicket.length}</span>
                          </div>
                        </div>
                        <div className="p-3 space-y-3">
                          {needsTicket.map(job => (
                            <div key={job.id} onClick={() => navigate('/tickets', { state: { newTicket: true, jobId: job.id } })} className="bg-fieldiq-dark rounded-lg p-4 border border-l-4 border-fieldiq-border border-l-fi-signal-blue hover:border-fieldiq-accent/50 hover:border-l-fi-signal-blue cursor-pointer transition-all">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full bg-fi-signal-blue flex-shrink-0" />
                                <h4 className="font-semibold text-white text-sm">{job.customer?.name || 'No Customer'} {job.job_number}</h4>
                              </div>
                              {job.service_type && <p className="text-fieldiq-text text-xs font-medium ml-4">{job.service_type}</p>}
                              <p className="text-xs text-fieldiq-muted ml-4 mt-1">Tap to create ticket</p>
                            </div>
                          ))}
                          {needsTicket.length === 0 && <p className="text-xs text-fieldiq-muted text-center py-4">No completed jobs waiting</p>}
                        </div>
                      </div>
                    );
                  })()}

                  {/* NEEDS INVOICE column — jobs with approved tickets but not invoiced */}
                  {(() => {
                    const needsInvoice = jobs.filter(j => (j.approved_ticket_count || 0) > 0 && (j.invoiced_ticket_count || 0) === 0);
                    return (
                      /* Same treatment as NEEDS TICKET above, on green: the hue
                         is the badge fill, the dot and the left edge, and never
                         the ink. The 400-weight green ink was #4ade80 on a white
                         panel at 1.6. */
                      <div className="bg-fieldiq-card rounded-fi-panel min-h-[600px] w-[280px] flex-shrink-0 border border-fieldiq-border">
                        <div className="p-4 border-b border-fieldiq-border">
                          <div className="flex items-center justify-between">
                            <h3 className="text-white font-semibold text-sm uppercase">NEEDS INVOICE</h3>
                            <span className="bg-fi-signal-green text-fi-signal-ink border border-black/20 px-2 py-0.5 rounded-fi-pill text-xs font-extrabold">{needsInvoice.length}</span>
                          </div>
                        </div>
                        <div className="p-3 space-y-3">
                          {needsInvoice.map(job => (
                            <div key={job.id} onClick={() => navigate(`/invoices/generate`, { state: { jobId: job.id } })} className="bg-fieldiq-dark rounded-lg p-4 border border-l-4 border-fieldiq-border border-l-fi-signal-green hover:border-fieldiq-accent/50 hover:border-l-fi-signal-green cursor-pointer transition-all">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-2 h-2 rounded-full bg-fi-signal-green flex-shrink-0" />
                                <h4 className="font-semibold text-white text-sm">{job.customer?.name || 'No Customer'} {job.job_number}</h4>
                              </div>
                              {job.service_type && <p className="text-fieldiq-text text-xs font-medium ml-4">{job.service_type}</p>}
                              <p className="text-xs text-fieldiq-muted ml-4 mt-1">{job.approved_ticket_count} ticket{(job.approved_ticket_count || 0) > 1 ? 's' : ''} ready</p>
                            </div>
                          ))}
                          {needsInvoice.length === 0 && <p className="text-xs text-fieldiq-muted text-center py-4">No tickets waiting</p>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })()}

            {/* MONTH VIEW */}
            {timeView === 'month' && (
              <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                {monthWeeks.map((ws, idx) => {
                  const weekEnd = addDays(ws, 6);
                  const weekJobs = getJobCardsForWeek(ws);
                  return (
                    <div key={idx} className="bg-fieldiq-card rounded-fi-panel border border-fieldiq-border min-h-[600px] w-[320px] flex-shrink-0">
                      <div className="p-4 border-b border-fieldiq-border">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-semibold text-sm uppercase">WEEK {idx + 1}</h3>
                            <p className="text-fieldiq-muted text-xs mt-1">{format(ws, 'MMM d')}–{format(weekEnd, 'd')}</p>
                          </div>
                          <span className="bg-fieldiq-border text-fieldiq-muted px-2 py-1 rounded-full text-xs">{weekJobs.length}</span>
                        </div>
                      </div>
                      <div className="p-3 space-y-3">
                        {/* A card is passed only for a CARRIED day, so a week
                            column reads as it always did until a phase runs into
                            it — then that card says "Day N" and is dimmed. Never
                            draggable and never carrying Start/End: the week
                            board owns both, and this column is a whole week, so
                            a control here would have no day to act on. */}
                        {weekJobs.map(entry => (
                          <JobCard
                            key={entry.dragId}
                            job={entry.job}
                            /* A day-1 card keeps the job's own test id, as this
                               column and the Status board have always had it; a
                               carried card is a second card for the same job and
                               is identified by the day it carries into. */
                            dragId={entry.isContinuation ? entry.dragId : undefined}
                            cardType={entry.isContinuation ? entry.cardType : undefined}
                            card={entry.isContinuation ? { ...entry, showsControls: false } : undefined}
                          />
                        ))}
                        <button className="w-full py-3 border-2 border-dashed border-fieldiq-border rounded-fi-control text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-text transition flex items-center justify-center gap-2">
                          <Plus className="w-4 h-4" /> Add card
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* STATUS VIEW */}
            {timeView === 'status' && (
              <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                {statusColumns.map((status) => {
                  const statusJobs = getJobsForStatus(status);
                  return (
                    <DroppableColumn key={status} id={`col-status-${status}`}>
                      <div className="p-4 border-b border-fieldiq-border">
                        <div className="flex items-center justify-between">
                          <h3 className="text-white font-semibold text-sm uppercase">{statusLabels[status]}</h3>
                          <span className="bg-fieldiq-border text-fieldiq-muted px-2 py-1 rounded-full text-xs">{statusJobs.length}</span>
                        </div>
                      </div>
                      <div className="p-3 space-y-3">
                        {statusJobs.map(job => <DraggableJobCard key={job.id} job={job} isDragOverlay={false} />)}
                        <button className="w-full py-3 border-2 border-dashed border-fieldiq-border rounded-fi-control text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-text transition flex items-center justify-center gap-2">
                          <Plus className="w-4 h-4" /> Add card
                        </button>
                      </div>
                    </DroppableColumn>
                  );
                })}
              </div>
            )}

            {/* NEEDS PDF VIEW */}
            {timeView === 'pdf' && (
              /* THE REGISTER IDIOM: one panel, one amber 4px left edge on the
                 PANEL, an eyebrow plus an h2, and no footer — a footer earns
                 its place by carrying a count, a scope, a permission or a
                 timestamp, and the count is already in the subhead. */
              <div className="rounded-fi-panel border border-l-4 border-fieldiq-border border-l-fi-amber bg-fieldiq-card">
                <div className="border-b border-fieldiq-border px-4 py-4">
                  <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Torque monitoring</p>
                  <h2 className="text-lg font-extrabold text-white">Needs PDF</h2>
                  {/* The count is a dash until the queue answers — "0 jobs
                      awaiting" is a claim this screen cannot make off a failed
                      read, and it is the reassuring one. */}
                  <p className="text-sm text-fieldiq-muted">
                    {displayValue(torqueArrived ? torqueSheets.length : null)} job{torqueSheets.length !== 1 ? 's' : ''} awaiting torque sheet PDF
                  </p>
                </div>
                {torqueFailure ? (
                  <LoadFailureNotice
                    error={queryFailureError(torqueQuery)}
                    kind={torqueFailure}
                    subject="the torque sheet queue"
                    allowed="admins, office staff and dispatchers"
                  />
                ) : !torqueArrived ? (
                  <div className="flex justify-center py-12">
                    <Loader size={28} className="animate-spin text-fieldiq-accent" aria-label="Loading torque sheets" />
                  </div>
                ) : torqueSheets.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText size={48} className="mx-auto mb-3 text-fieldiq-muted" aria-hidden="true" />
                    <p className="text-fieldiq-text font-medium">No pending torque sheets</p>
                    <p className="text-fieldiq-muted text-sm mt-1">Jobs with Torque Monitoring will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[960px] text-sm">
                      <thead>
                        <tr className="border-b border-fieldiq-border text-fieldiq-text text-xs uppercase tracking-wider">
                          <th className="text-left px-4 py-3 font-medium">Job #</th>
                          <th className="text-left px-4 py-3 font-medium">Client</th>
                          <th className="text-left px-4 py-3 font-medium">Rig / Well</th>
                          <th className="text-left px-4 py-3 font-medium">Casing</th>
                          <th className="text-left px-4 py-3 font-medium">Thread</th>
                          <th className="text-left px-4 py-3 font-medium">Date</th>
                          <th className="text-left px-4 py-3 font-medium">Crew</th>
                          <th className="text-right px-4 py-3 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {torqueSheets.map((ts) => (
                          <tr
                            key={ts.id}
                            /* The neutral page-surface hover, not a white
                               wash — a white wash on a white panel in light
                               mode is no hover at all. */
                            className="border-b border-fieldiq-border/50 hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 focus-visible:outline-none transition-colors cursor-pointer"
                            onClick={() => navigate(`/jobs/${ts.job_id}`)}
                          >
                            <td className="px-4 py-3">
                              <span className="text-fieldiq-accent font-medium">{ts.job_number}</span>
                            </td>
                            <td className="px-4 py-3 text-white">{displayValue(ts.customer_name)}</td>
                            <td className="px-4 py-3 text-white">{ts.rig_name || ts.well_name || '—'}</td>
                            <td className="px-4 py-3 text-fieldiq-text">
                              {ts.casing_size_inches ? `${ts.casing_size_inches}" ${ts.casing_weight_lb_ft ? `${ts.casing_weight_lb_ft}#` : ''}` : '—'}
                            </td>
                            <td className="px-4 py-3">
                              {ts.thread_type ? (
                                <span className="bg-fieldiq-dark px-2 py-0.5 rounded text-xs text-fieldiq-text">{ts.thread_type}</span>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-fieldiq-text">
                              {formatCalendarDate(ts.scheduled_date) ?? '—'}
                            </td>
                            <td className="px-4 py-3">
                              {ts.crew && ts.crew.length > 0 ? (
                                <div className="flex -space-x-1.5">
                                  {ts.crew.slice(0, 4).map(c => (
                                    <div key={c.id} className="w-6 h-6 rounded-full bg-fieldiq-accent/20 border border-fieldiq-border flex items-center justify-center" title={`${c.first_name} ${c.last_name}`}>
                                      <span className="text-[9px] font-bold text-fieldiq-accent">{c.initials}</span>
                                    </div>
                                  ))}
                                  {ts.crew.length > 4 && (
                                    <div className="w-6 h-6 rounded-full bg-fieldiq-dark border border-fieldiq-border flex items-center justify-center">
                                      <span className="text-[9px] text-fieldiq-muted">+{ts.crew.length - 4}</span>
                                    </div>
                                  )}
                                </div>
                              ) : '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={(e) => { e.stopPropagation(); clearTorqueSheet.mutate(ts.id); }}
                                disabled={clearTorqueSheet.isPending}
                                /* A 600-weight green fill under plain white
                                   ink measures 3.0 — and in light mode theme.css
                                   turns that white into dark navy, which is
                                   worse. The signal fill plus `--fi-signal-ink`
                                   is the idiom that holds in both halves. */
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-fi-signal-green border border-black/20 text-fi-signal-ink rounded-fi-control text-xs font-extrabold transition hover:brightness-95 disabled:opacity-50"
                              >
                                <CheckCircle className="w-3.5 h-3.5" />
                                Clear
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
          {/* THE OVERLAY MUST RENDER THE SAME CARD THE COLUMN DOES. Picking a
              card up should move it, not redraw it.

              `cardType` IS NOW PASSED, and was not before. It was already in the
              drag payload (`data: { job, cardType }`) and simply never read
              here, so a WASH card lost its blue border and its 🔫 WASH label the
              instant you picked it up and became a run card mid-drag. That was
              pre-existing; it is fixed here because this PR adds a left stripe
              to the same edge, and shipping a stripe that survives the drag
              while the wash treatment still vanished would have looked
              deliberate.

              The status stripe needs nothing extra — `JobCard` derives it from
              `job.status`, which the overlay already had. The crew chips read
              their specialty from the same `job.crew`, so their badges match
              too. */}
          <DragOverlay>
            {activeDragType === 'job' && activeJob ? (
              <JobCard job={activeJob} cardType={activeCardType} isOverlay />
            ) : activeDragType === 'crew' && activeCrewMember ? (
              <span className={`${crewChipClass(activeCrewMember.specialty)} shadow-lg`}>
                <CrewChipBadge specialty={activeCrewMember.specialty} />
                {[activeCrewMember.first_name, activeCrewMember.last_name].filter(n => n && n.trim()).join(' ')}
              </span>
            ) : activeDragType === 'roster' && activeCrewMember ? (
              <span className={`${ROSTER_CHIP_BASE} ${crewSpecialtyTint(activeCrewMember.specialty)} shadow-lg`}>
                {[activeCrewMember.first_name, activeCrewMember.last_name].filter(n => n && n.trim()).join(' ')}
              </span>
            ) : null}
          </DragOverlay>
          </DndContext>
        )}

        {/* EMPTY STATE — only once the list actually ARRIVED. An empty array is
            "none" and says so; a failed or refused read is not "none" and is
            handled by the notice above. */}
        {jobsArrived && jobs.length === 0 && (
          <div className="text-center py-12">
            <LayoutDashboard size={48} className="mx-auto mb-3 text-fieldiq-muted" />
            <p className="text-fieldiq-text font-medium">No dispatch jobs found</p>
            <p className="text-fieldiq-muted text-sm mt-1">Create a job and assign a scheduled date to see it here</p>
          </div>
        )}

        {/* CALENDAR VIEW — gated on the same failure branch as the board. It
            used to render regardless, so a refused or failed load drew 42 empty
            day cells, which is the reassuring answer to a question this screen
            could not answer. */}
        {!isLoading && !jobsFailure && viewMode === 'calendar' && (() => {
          const calMonthStart = startOfMonth(currentDate);
          const calMonthEnd = endOfMonth(currentDate);
          const calStart = startOfWeek(calMonthStart, { weekStartsOn: 0 });
          const calDays: Date[] = [];
          let cursor = calStart;
          while (calDays.length < 42) {
            calDays.push(cursor);
            cursor = addDays(cursor, 1);
            if (calDays.length >= 35 && cursor > calMonthEnd) break;
          }
          const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
          return (
            <div className="bg-fieldiq-card rounded-fi-panel border border-fieldiq-border overflow-hidden">
              {/* Month header */}
              <div className="px-4 py-3 border-b border-fieldiq-border flex items-center justify-between">
                <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="p-1 text-fieldiq-muted transition hover:text-fieldiq-text"><ChevronLeft className="w-5 h-5" /></button>
                <h3 className="text-white font-semibold">{format(currentDate, 'MMMM yyyy')}</h3>
                <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="p-1 text-fieldiq-muted transition hover:text-fieldiq-text"><ChevronRight className="w-5 h-5" /></button>
              </div>
              {/* Day name headers */}
              <div className="grid grid-cols-7 border-b border-fieldiq-border">
                {dayNames.map(d => (
                  <div key={d} className="py-2 text-center text-xs text-fieldiq-muted font-medium uppercase">{d}</div>
                ))}
              </div>
              {/* Day cells */}
              <div className="grid grid-cols-7">
                {calDays.map((day, i) => {
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  // The tenant's day, not the browser's — as the week board.
                  const isToday = tenantToday !== null && format(day, 'yyyy-MM-dd') === tenantToday;
                  const dayJobs = getJobCardsForDay(day);
                  // `format` and not `toISOString().slice(0,10)`: the latter is
                  // UTC's date, so west of Greenwich every cell after 18:00
                  // local would key on tomorrow and the toggle would open the
                  // wrong day. `day` is already a locally-constructed calendar
                  // day (see calDays above).
                  const dayKey = format(day, 'yyyy-MM-dd');
                  const isExpanded = expandedDays.has(dayKey);
                  const hiddenCount = dayJobs.length - CALENDAR_PILL_CAP;
                  const visibleJobs = isExpanded ? dayJobs : dayJobs.slice(0, CALENDAR_PILL_CAP);
                  return (
                    <div
                      key={i}
                      // "Outside this month" is said by the CELL — the recessed
                      // background and the muted date number — AND, since the
                      // pills went solid, by the pill's fill at 80%.
                      //
                      // #391 established that the pill should stay at full
                      // strength and that `opacity` was the wrong lever, and
                      // half of that still holds: `opacity` fades a pill's text
                      // and its background together, so it cannot buy contrast
                      // back (at the original 0.3, the label measured 1.73:1).
                      // The other half did not survive the solid fill. The
                      // recessed cell is DARKER than the in-month card, so an
                      // undimmed solid pill measured 6.93-10.56 against a
                      // trailing cell versus 5.75-8.76 in-month — the September
                      // job was the loudest thing in an August grid, which is
                      // backwards. `cal-pill-trailing` dims the FILL ONLY and
                      // leaves the label opaque, which is the thing plain
                      // `opacity` could not do.
                      //
                      // `bg-fieldiq-dark` is the recess in both themes: darker
                      // than `--fieldiq-card` in dark (#12182b vs #1e293b) and
                      // in light (#EDEFF2 vs #FFFFFF).
                      className={`min-h-[100px] border-b border-r border-fieldiq-border p-1.5 ${!isCurrentMonth ? 'bg-fieldiq-dark' : ''}`}
                    >
                      <div className={`text-xs mb-1 ${isToday ? 'bg-fieldiq-accent text-fieldiq-dark w-6 h-6 rounded-full flex items-center justify-center font-bold' : isCurrentMonth ? 'text-fieldiq-text' : 'text-fieldiq-muted'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className="space-y-1">
                        {/* SOLID FILL, NOT A TINT. `jobStatusStyle` pairs the
                            status hue at full strength with `text-status-fg`,
                            which flips per theme — near-black on a bright
                            fill in dark, white on a dark fill in light. A wash
                            measured 1.23-1.44:1 against its own cell, so a week
                            with jobs on every day read as one continuous light
                            smear; the fill measures 4.35-10.56 and the label
                            5.02-10.56.

                            THE DOT IS GONE FROM THIS PILL. It was
                            `bg-status-<hue>` on a 20% wash of the same hue; on a
                            SOLID fill of that hue it is the identical colour,
                            contrast 1.00, an invisible element with a layout
                            cost. The signal it carried moved into the fill and
                            got stronger doing it — `scheduled` vs `on_hold`
                            separate at dE 18.9 as solids against 9.5 as tints.
                            The two dots on JobsScreen and the one on JobCard
                            below survive, because those surfaces have no pill.

                            NEVER `text-white` here. theme.css remaps
                            `.text-white` in light mode to `--fieldiq-heading`,
                            which would render dark navy on these fills at
                            1.52-3.11:1. That is what the token is for.

                            `cal-pill-trailing` dims the fill — and only the
                            fill, never the label — on out-of-month cells. See
                            the rule and the measured 80% at the bottom of
                            theme.css. */}
                        {/* A carried-over day says "Day N" and takes the dimmed
                            fill, which leaves the label at full contrast. */}
                        {/* THE PHASE IS NAMED ONLY WHEN THE JOB HAS TWO TRIPS.
                            A job whose wash is on a day of its own drew two
                            identical pills, one per trip, and neither said which
                            was which; the week board's card has always said it
                            with its WASH / RUN badge. `crewSplit` is the board's
                            own "this job draws a wash card" answer, so the
                            calendar labels exactly the jobs the board splits.
                            A same-day wash is one trip, and "Run" on every pill
                            would be noise on the commonest case. Plain words, no
                            emoji: the cell is ~110px of 10px text.

                            A labelled pill's accessible name carries the phase
                            and the day, so the two pills of one job do not share
                            a name; a one-trip pill keeps its name as it was. */}
                        {visibleJobs.map(({ job, cardType, crewSplit, isContinuation, dayNumber }) => {
                          const name = job.customer?.name || job.title || job.job_number;
                          const phase = crewSplit ? (cardType === 'wash' ? 'Wash' : 'Run') : null;
                          return (
                            <button
                              key={job.id}
                              data-testid={`cal-pill-${job.id}-${dayKey}`}
                              aria-label={phase ? [phase, name, isContinuation ? `Day ${dayNumber}` : null, format(day, 'MMM d')].filter(Boolean).join(' — ') : undefined}
                              onClick={() => navigate(`/jobs/${job.id}`, { state: { from: '/dispatch' } })}
                              className={`w-full text-left text-[10px] px-1.5 py-1 rounded truncate font-medium ${jobStatusStyle(job.status)} ${!isCurrentMonth || isContinuation ? 'cal-pill-trailing' : ''} hover:ring-1 hover:ring-inset hover:ring-current transition`}
                            >
                              {phase && `${phase} · `}
                              {name}
                              {isContinuation && ` · Day ${dayNumber}`}
                            </button>
                          );
                        })}
                        {/* This was a plain <div>, which made it a dead end: a
                            day with 7 jobs said "+4 more" and offered no way to
                            reach any of them from this view — not a click, not an
                            expand, not a link. Reported live on Aug 18.

                            `stopPropagation` because the pills above are also
                            buttons and this sits in the same stack; without it a
                            future click handler on the cell (a day view, say)
                            would fire on every expand. It renders AFTER the pill
                            stack rather than wrapping it, so it never swallows a
                            pill's own click.

                            An expand and not a modal or a route: the point of the
                            month grid is comparing days against each other, and
                            both of those replace the comparison with a single
                            day. */}
                        {hiddenCount > 0 && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleDayExpanded(dayKey); }}
                            aria-expanded={isExpanded}
                            // The visible label is deliberately terse — the cell
                            // is ~110px wide and the pills are 10px text. The
                            // accessible name carries the day, because up to 42
                            // of these render at once and "Show less" on its own
                            // tells a screen reader user nothing about WHICH day
                            // they are collapsing.
                            aria-label={isExpanded
                              ? `Show fewer jobs on ${format(day, 'MMMM d')}`
                              : `Show ${hiddenCount} more job${hiddenCount === 1 ? '' : 's'} on ${format(day, 'MMMM d')}`}
                            className="w-full text-left text-[10px] text-fieldiq-muted hover:text-foreground focus-visible:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-current rounded pl-1 transition"
                          >
                            {isExpanded ? 'Show less' : `+${hiddenCount} more`}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
        </div>
      </section>
    </div>
  );
};

/**
 * THE OUTER TAB STRIP — which question the page answers, board or calendar.
 *
 * `aria-pressed` rather than the tab/tablist/tabpanel pattern, and that is the
 * shape ApprovalQueueScreen's ViewStrip settled on for the same reason: the two
 * views are separate URLs (`?view=`), each independently linkable, which is a
 * set of toggle buttons and not a roving-tabindex widget over one panel.
 *
 * THE ACTIVE TAB IS NOT MARKED BY INK COLOUR. `--fieldiq-accent-signal` is
 * #b45309, which measures 4.36 on `--fieldiq-dark` in light — under the floor
 * on the one surface a body strip actually sits on. The amber underline and the
 * weight carry the state; the word stays `--fieldiq-text`.
 */
function ViewStrip({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
  const items: { key: ViewMode; label: string; icon: React.ReactNode }[] = [
    { key: 'board', label: 'Kanban', icon: <LayoutDashboard className="size-4" aria-hidden="true" /> },
    { key: 'calendar', label: 'Calendar', icon: <Calendar className="size-4" aria-hidden="true" /> },
  ];
  return (
    <div
      data-testid="dispatch-view-strip"
      role="group"
      aria-label="Switch view"
      className="flex gap-1 border-b border-fieldiq-border"
    >
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={() => onChange(it.key)}
          aria-pressed={view === it.key}
          className={`relative flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
            view === it.key
              ? 'font-extrabold text-fieldiq-text'
              : 'font-semibold text-fieldiq-muted hover:text-fieldiq-text'
          }`}
        >
          {it.icon}
          {it.label}
          {view === it.key && (
            <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-fi-amber" />
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * THE INNER TAB STRIP — which board. Smaller and sitting against the board it
 * re-titles, so the nesting reads as nesting rather than as two peers.
 *
 * NO COUNT ON ANY OF THESE, and "Needs PDF" is the one that used to carry one.
 * A tab switches which question the page answers, so a number on it is a count
 * of a dataset the reader is not currently looking at — the torque-sheet queue
 * while you are reading Tuesday. The figure moved to the band, where it is on
 * screen in every view rather than only while this strip is drawn.
 */
function BoardTabs({ tab, onChange }: { tab: TimeView; onChange: (t: TimeView) => void }) {
  const items: { key: TimeView; label: string }[] = [
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'status', label: 'Status' },
    { key: 'pdf', label: 'Needs PDF' },
  ];
  return (
    <div
      data-testid="dispatch-board-tabs"
      role="group"
      aria-label="Switch board"
      className="flex gap-1 border-b border-fieldiq-border"
    >
      {items.map((it) => (
        <button
          key={it.key}
          type="button"
          onClick={() => onChange(it.key)}
          aria-pressed={tab === it.key}
          className={`relative px-3 py-1.5 text-xs transition-colors ${
            tab === it.key
              ? 'font-extrabold text-fieldiq-text'
              : 'font-semibold text-fieldiq-muted hover:text-fieldiq-text'
          }`}
        >
          {it.label}
          {tab === it.key && (
            <span aria-hidden="true" className="absolute bottom-0 left-0 right-0 h-[2px] bg-fi-amber" />
          )}
        </button>
      ))}
    </div>
  );
}

// JOB CARD COMPONENT
// Droppable column wrapper
const DroppableColumn: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`bg-fieldiq-card rounded-fi-panel border border-fieldiq-border min-h-[600px] w-[280px] flex-shrink-0 transition-all ${
        isOver ? 'ring-2 ring-fieldiq-accent/60 bg-fieldiq-accent/5' : ''
      }`}
    >
      {children}
    </div>
  );
};

// The roster groups crew under this key when `position_name` is null. It
// renders as "No Position Set" — a real group of people with no primary
// position recorded, not a missing heading.
const UNSPECIFIED_KEY = 'Unspecified';

// Specialty classification now lives in src/lib/crew-specialty.ts, keyed on the
// API's four-value `employee_specialty` enum. What used to be here — `crewGroup`
// plus `WASH_SPECIALTY_KEYS` — collapsed those four onto TWO colours and painted
// "no specialty set" identically to run crew. See that file for the full account,
// including the two dead keys the substring match was carrying.
//
// It exists to LABEL chips, never to hide them: a card used to render only the
// specialties matching its own wash/run type, so a wash tech assigned to a run
// card was invisible — and invisible chips are not draggable, so nobody could
// take them back off. Every card renders the job's full crew.
//
// role_on_job is deliberately not consulted: it is null for every crew member
// across every tenant, so the old `specialty || role_on_job` was dead code.

const CREW_CHIP_BASE = 'text-xs px-1.5 py-0.5 rounded font-medium select-none whitespace-nowrap';

/**
 * THE ON-CARD CHIP WEARS ITS ROLE'S FILL NOW, AND KEEPS THE LETTER BADGE.
 *
 * Both halves of that sentence are load-bearing and neither is decoration:
 *
 *   THE FILL is `--crew-*`, the same value the roster chip spends, so one
 *   person is one colour everywhere on this board. It could not be before: the
 *   old crew hues collided with the statuses this card sits in — a wash chip
 *   resolved to #60a5fa in dark, byte-identical to `--status-pending`, and
 *   `--crew-tong` was byte-identical to `--status-on-hold`. The hues were
 *   re-picked against all seventeen hues the board can put beside them (six
 *   statuses per theme plus the five signal fills), floor dE 12, so the chip no
 *   longer competes with the 4px stripe on its own left edge.
 *
 *   THE BADGE is what now separates a PLACED chip from a DRAGGABLE one. That
 *   job used to belong to outlined-versus-filled, and both chips are filled
 *   now, so removing the badge would silently delete the distinction rather
 *   than tidy a duplicate. It also states the specialty, which a card says
 *   nowhere else.
 *
 * See the "ONE PERSON, ONE HUE" note in lib/crew-specialty.ts before making
 * either surface agree with the other.
 */
const crewChipClass = (specialty?: string | null) => `${CREW_CHIP_BASE} ${crewSpecialtyTint(specialty)}`;

/**
 * The letter badge, rendered inside the chip ahead of the name.
 *
 * `tracking-wide` because these are all-caps abbreviations at 10px, where the
 * default tracking closes up glyph pairs. It is a legibility aid and NOT what
 * makes the four distinguishable — that is the letter set itself (see
 * CREW_SPECIALTY_BADGE, where `TT` was rejected for being a subsequence of
 * `TWT`). Spacing cannot rescue a containment relationship; picking letters that
 * do not contain each other can.
 *
 * A member with no specialty renders NO badge rather than a placeholder one. An
 * empty slot reads as "nothing recorded", which is true; a "—" badge would read
 * as a specialty called "—".
 */
// `| null` rather than bare optional: users.specialty is a nullable column, so
// an unset specialty arrives as null and never as undefined. The runtime was
// always right — crewSpecialtyBadge takes `unknown` and normalizes — but the
// prop type said the state could not occur. Widening DispatchCrewMember.specialty
// to match the wire is what surfaced this and its three call sites.
const CrewChipBadge: React.FC<{ specialty?: string | null }> = ({ specialty }) => {
  const badge = crewSpecialtyBadge(specialty);
  if (!badge) return null;
  // NO COLOUR CLASS: the badge INHERITS the chip's near-black, which is the
  // only ink measured against these fills. It was a muted grey while the chip
  // was unfilled, and a theme-following grey on a bright fill is the failure
  // CLAUDE.md records for signal hues in the opposite direction — the class is
  // in the DOM, the build is clean, and the letters are simply not there.
  // Weight, not colour, is what sets it apart from the name beside it.
  return (
    <span className="text-[10px] font-bold tracking-wide mr-1">
      {badge}
    </span>
  );
};

// Grouped by specialty so a mixed crew reads as blocks rather than an
// interleaved list, in the order a dispatcher reads a crew: supervisor, tong,
// computer, wash. Anyone with no specialty sorts last. This used to be a
// three-value wash/run/unassigned rank; it is four plus the null now, matching
// the badges on the chips — see CREW_SPECIALTY_ORDER in lib/crew-specialty.ts.
const sortCrew = (crew: CrewMember[]): CrewMember[] =>
  [...crew].sort((a, b) =>
    crewSpecialtyRank(a.specialty) - crewSpecialtyRank(b.specialty) ||
    (a.last_name ?? '').localeCompare(b.last_name ?? '') ||
    (a.first_name ?? '').localeCompare(b.first_name ?? '')
  );

// The crew-shaped half of a roster entry, for the card to draw a just-added
// hand with their specialty before the board refetches.
const rosterCrewMember = (m: any): CrewMember => ({
  id: m.id, first_name: m.first_name, last_name: m.last_name, initials: m.initials ?? null, specialty: m.specialty ?? null,
});

// Pinned CREW roster column — also a drop target (drag a crew chip here to unassign).
// Must live outside DispatchScreen so useDroppable binds to the live DndContext.
const RosterColumn: React.FC<{
  rosterByPosition: [string, any[]][];
  count: number;
  /** Classified, not a boolean — a refusal and a dead zone want different words. */
  failure: LoadFailureKind | null;
}> = ({ rosterByPosition, count, failure }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'roster-dropzone',
    data: { type: 'roster' },
  });
  return (
    <div
      ref={setNodeRef}
      className={`bg-fieldiq-card rounded-fi-panel border border-fieldiq-border min-h-[600px] w-[280px] flex-shrink-0 sticky left-0 z-20 transition-all ${
        isOver ? 'ring-2 ring-fieldiq-accent/60' : ''
      }`}
    >
      <div className="p-4 border-b border-fieldiq-border">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold text-sm uppercase">CREW</h3>
          <span className="bg-fieldiq-border text-fieldiq-muted px-2 py-1 rounded-full text-xs">{count}</span>
        </div>
      </div>
      <div className="p-3 space-y-3">
        {rosterByPosition.map(([position, members]) => (
          <div key={position}>
            <p className="text-[10px] text-fieldiq-muted font-semibold uppercase tracking-wide mb-1.5">{position === UNSPECIFIED_KEY ? 'No Position Set' : position}</p>
            <div className="flex flex-wrap gap-1">
              {members.map((member: any) => (
                <RosterCrewChip key={member.id} crewMember={member} />
              ))}
            </div>
          </div>
        ))}
        {/* "No assignable crew" is a fact about the roster and is only sayable
            once the roster arrived. A failed or refused read is not an empty
            yard, and on this column an empty yard means nobody can be dragged
            onto a job. */}
        {failure ? (
          <LoadFailureNotice
            error={null}
            kind={failure}
            subject="the crew roster"
            allowed="admins, office staff and dispatchers"
            className="px-2 py-6"
          />
        ) : count === 0 ? (
          <p className="text-xs text-fieldiq-muted text-center py-4">No assignable crew</p>
        ) : null}
      </div>
    </div>
  );
};

// Draggable crew chip
const DraggableCrewChip: React.FC<{ crewMember: CrewMember; cardId: string; jobId: string; cardType?: CardType; split: boolean }> = ({ crewMember, cardId, jobId, cardType, split }) => {
  const displayName = [crewMember.first_name, crewMember.last_name]
    .filter(n => n && n.trim())
    .join(' ');
  const fallbackInitials = crewMember.last_name?.trim()
    ? `${crewMember.first_name?.[0] || ''}${crewMember.last_name[0]}`
    : (crewMember.first_name?.[0] || '');

  const jobPosition = jobPositionForCard(crewMember.positions, cardType, split);

  const chipData: CrewChipDragData = {
    type: 'crew', userId: crewMember.id, sourceJobId: jobId, crewMember, sourceCardType: cardType, sourceSplit: split,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `crew-${cardId}-${crewMember.id}`,
    data: chipData,
  });

  return (
    <span
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onPointerDown={(e) => { e.stopPropagation(); listeners?.onPointerDown?.(e); }}
      onTouchStart={(e: any) => { e.stopPropagation(); listeners?.onTouchStart?.(e); }}
      // The tooltip carries the specialty in full. The badge is an
      // abbreviation, and an abbreviation nobody can expand is a puzzle — this
      // is where `TNG` becomes "Tong Technician" without spending card width.
      title={`${displayName}${crewSpecialtyBadge(crewMember.specialty) ? ` — ${crewSpecialtyLabel(crewMember.specialty)}` : ''}${jobPosition ? ` · on this job: ${jobPosition}` : ''}`}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: 'grab', touchAction: 'none' }}
      className={crewChipClass(crewMember.specialty)}
    >
      <CrewChipBadge specialty={crewMember.specialty} />
      {displayName || fallbackInitials}
      {/* The hand's position ON THIS JOB (API 0426) — this card's phase on a
          split job. No tag when none is picked. Inherits the chip's ink, the
          only ink measured on these fills; the edge is the badge idiom's. */}
      {jobPosition && (
        <span data-testid="crew-chip-job-position" className="ml-1 rounded-sm border border-black/20 px-1 text-[10px] font-semibold">
          {jobPosition}
        </span>
      )}
    </span>
  );
};

// Roster chip — filled with the role's hue, and IDENTICAL to the on-card chip
// but for the letter badge, which only the card carries. The roster is where
// you scan for a person and the card is where you scan for a job's crew, so the
// two still must not read as the same object: the badge is what carries that
// now, since outlined-versus-filled was spent when both surfaces took the fill.
// The unassigned chip keeps its 1px edge, since it has no fill to read as a shape.
const ROSTER_CHIP_BASE = 'text-xs px-1.5 py-0.5 rounded font-medium select-none whitespace-nowrap';

interface RosterCrewChipProps {
  crewMember: CrewMember;
}

const RosterCrewChip: React.FC<RosterCrewChipProps> = ({ crewMember }) => {
  const displayName = [crewMember.first_name, crewMember.last_name]
    .filter(n => n && n.trim())
    .join(' ');
  const fallbackInitials = crewMember.last_name?.trim()
    ? `${crewMember.first_name?.[0] || ''}${crewMember.last_name[0]}`
    : (crewMember.first_name?.[0] || '');

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `roster-${crewMember.id}`,
    data: { type: 'roster', userId: crewMember.id, crewMember },
  });

  return (
    <span
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onPointerDown={(e) => { e.stopPropagation(); listeners?.onPointerDown?.(e); }}
      onTouchStart={(e: any) => { e.stopPropagation(); listeners?.onTouchStart?.(e); }}
      title={`${displayName}${crewSpecialtyBadge(crewMember.specialty) ? ` — ${crewSpecialtyLabel(crewMember.specialty)}` : ''}`}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: 'grab', touchAction: 'none' }}
      // NO BADGE HERE, and that is the one difference from the card chip. The
      // roster's tint already carries the specialty and its group heading the
      // position, so a badge would repeat them — and it is what tells a placed
      // chip from a draggable one. See the "ONE PERSON, ONE HUE" note in
      // lib/crew-specialty.ts before adding one.
      className={`${ROSTER_CHIP_BASE} ${crewSpecialtyTint(crewMember.specialty)}`}
    >
      {displayName || crewMember.initials || fallbackInitials}
    </span>
  );
};

// Draggable + droppable wrapper around JobCard
// A day-column card passes `card`; the Pending and Status boards have no day
// and render the job's run card by its bare id.
const DraggableJobCard: React.FC<{ job: DispatchJob; isDragOverlay: boolean; card?: PhaseCard }> = ({ job, isDragOverlay, card }) => {
  const cardId = card?.dragId ?? job.id;
  const cardType = card?.cardType;
  const dragData: JobCardDragData = {
    type: 'job',
    job,
    jobId: job.id,
    cardType: cardType ?? 'run',
    day: card?.day ?? null,
  };
  // A continuation is not draggable: moving it would re-date a phase that has
  // already started. It stays a crew drop target — the crew is the job's.
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: cardId,
    data: dragData,
    disabled: card?.isContinuation === true,
  });
  const dropData: JobCardDropData = { type: 'jobcard', jobId: job.id, cardType, split: card?.crewSplit === true };
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `jobcard-${cardId}`,
    data: dropData,
  });

  return (
    <div
      ref={(node) => { setDragRef(node); setDropRef(node); }}
      {...listeners}
      {...attributes}
      style={{ opacity: isDragging ? 0.2 : 1, cursor: card?.isContinuation ? 'default' : 'grab' }}
      className={isOver ? 'ring-2 ring-fieldiq-accent/60 rounded-lg' : ''}
    >
      <JobCard job={job} cardType={cardType} dragId={cardId} card={card} />
    </div>
  );
};

const JobCard: React.FC<{ job: DispatchJob; cardType?: CardType; dragId?: string; isOverlay?: boolean; card?: PhaseCard }> = ({ job, cardType, dragId, isOverlay, card }) => {
  const navigate = useNavigate();
  const phaseClockMutation = usePhaseClockMutation();
  const cancelJobMutation = useCancelJobMutation();
  const resumePhaseMutation = useResumePhaseMutation();
  // The phase this card is about. Pending and Status cards carry no phase and
  // stand for the run, which is what scheduled_date plans.
  const phase: CardType = cardType ?? 'run';
  const [menuOpen, setMenuOpen] = useState(false);
  const [crewPhaseOpen, setCrewPhaseOpen] = useState(false);
  const [clearStartOpen, setClearStartOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  /**
   * "Clear start" — on a card whose phase is STARTED AND NOT ENDED, for admin /
   * office_staff / dispatcher (DELETE .../phase/:phase/start, api#1028). The
   * card is drawn on its start day, so a wrongly recorded start pins it there
   * whatever the scheduled date says (TNT J-4372).
   *
   * Not on a carried-over run start: that is the API's inference from a
   * pre-0403 started_at, not a tap, and the route refuses it. Only phase cards
   * carry a clock; the Pending and Status boards offer nothing.
   *
   * Not on a RESUMED phase either (two or more stints, API 0534): its start is
   * history behind a closed stint, and the route refuses with a 409.
   */
  const clearableStart =
    card != null &&
    card.clock.startedAt != null &&
    card.clock.endedAt == null &&
    !card.clock.startCarriedOver &&
    (job.phase_stints?.[card.cardType]?.length ?? 0) <= 1 &&
    canClearPhaseStart(user?.role);
  /**
   * "Resume" — on a phase card whose phase has ENDED, on a job not closed
   * (API 0534). The crew is back after a gap: a new stint opens today and the
   * gap days stay empty. The route refuses anything else with a sentence.
   */
  const resumable = card != null && phaseIsResumable(job, card.cardType);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const handleAction = (action: string) => {
    setMenuOpen(false);
    switch (action) {
      case 'edit':
        navigate(`/jobs/${job.id}`, { state: { from: '/dispatch' } });
        break;
      case 'assign-crew':
        navigate(`/jobs/${job.id}`, { state: { section: 'crew', from: '/dispatch' } });
        break;
      case 'crew-phase':
        setCrewPhaseOpen(true);
        break;
      case 'assign-equipment':
        navigate(`/jobs/${job.id}`, { state: { section: 'equipment', from: '/dispatch' } });
        break;
      case 'start':
        phaseClockMutation.mutate({ jobId: job.id, phase, action: 'start', jobNumber: job.job_number });
        break;
      case 'create-ticket':
        navigate('/tickets', { state: { newTicket: true, jobId: job.id } });
        break;
      case 'call':
        if (job.customer_contact_phone) {
          window.location.href = `tel:${job.customer_contact_phone}`;
        }
        break;
      case 'clear-phase-start':
        setClearStartOpen(true);
        break;
      case 'resume-phase':
        if (card) resumePhaseMutation.mutate({ jobId: job.id, phase: card.cardType, jobNumber: job.job_number });
        break;
      case 'cancel':
        cancelJobMutation.mutate({ jobId: job.id, jobNumber: job.job_number });
        break;
    }
  };

  const menuItems = [
    { key: 'edit', label: 'Edit job', icon: Pencil },
    { key: 'assign-crew', label: 'Assign crew', icon: Users },
    // Only on a job with a wash date (same-day included): with no wash there
    // is only the run, and no phase to set.
    ...(hasWashDate(job) ? [{ key: 'crew-phase', label: 'Crew phase', icon: Users }] : []),
    { key: 'assign-equipment', label: 'Assign equipment', icon: Wrench },
    { key: 'start', label: phase === 'wash' ? 'Start wash' : 'Start run', icon: Play },
    ...(clearableStart ? [{ key: 'clear-phase-start', label: 'Clear start', icon: RotateCcw }] : []),
    ...(resumable ? [{ key: 'resume-phase', label: phase === 'wash' ? 'Resume wash' : 'Resume run', icon: RotateCw }] : []),
    { key: 'create-ticket', label: 'Create ticket', icon: Ticket },
    { key: 'call', label: 'Call customer', icon: Phone },
    { key: 'cancel', label: 'Cancel job', icon: XCircle },
  ];

  return (
    /**
     * THE LEFT EDGE IS THE STATUS. 4px, `border-l-status-<hue>`, measured
     * 4.35-10.56 against the card body across every status and both themes.
     *
     * It also fixes something the dot it replaced never touched: this card is
     * `bg-fieldiq-dark` inside a `bg-fieldiq-card` column, which measures 1.21:1
     * in dark and 1.15:1 in LIGHT — white on white, a card with no visible edge.
     * Every card now has one as a side effect of carrying its status.
     *
     * THE WASH BORDER IS ON THREE EDGES. It used to be a blue edge on all four,
     * and the status stripe wants the left one — the edge the eye tracks down a
     * stacked column. Sharing it would mean wash and run stop being
     * distinguishable exactly where the scan happens. Wash keeps three
     * independent channels that are not the left edge: the 🔫 WASH badge, the
     * three remaining borders, and its crew.
     *
     * THE EDGE IS A SIGNAL HUE NOW, NOT A RAW PALETTE VALUE. A 500-weight blue
     * from the raw palette has no light twin and no review behind it;
     * `--fi-signal-blue` is declared once for both halves, so a wash card's
     * edge is the same blue on a navy card and on a white one. It is a BORDER
     * and not ink — the signal hues are fills and edges, never text.
     *
     * The hover colour stays on those three edges too, so a wash card still
     * lights blue and a run card amber on hover. `border-l-4` overrides the 1px
     * `border` width on that side only.
     */
    <div
      /* A stable hook for the card element itself. The card is a styled <div>
         with no role and no accessible name, so its own classes — the status
         stripe, the wash border — are unreachable from a role or text query, and
         reaching them by walking up from the title is DOM access the lint
         ratchet rejects (correctly: it couples the test to the card's internal
         nesting). Keyed on the drag id rather than the job id because one job
         can render as TWO cards, a run and a wash. */
      data-testid={`job-card-${dragId || job.id}`}
      className={`bg-fieldiq-dark rounded-lg p-4 border border-l-4 ${jobStatusStripe(job.status)} transition-all cursor-pointer group relative ${card?.isContinuation ? 'opacity-60' : ''} ${
      cardType === 'wash'
        ? 'border-t-fi-signal-blue/40 border-r-fi-signal-blue/40 border-b-fi-signal-blue/40 hover:border-t-fi-signal-blue/70 hover:border-r-fi-signal-blue/70 hover:border-b-fi-signal-blue/70'
        : 'border-t-fieldiq-border border-r-fieldiq-border border-b-fieldiq-border hover:border-t-fieldiq-accent/50 hover:border-r-fieldiq-accent/50 hover:border-b-fieldiq-accent/50'
    }`}>
      {/* THE PHASE IS A BADGE, NOT COLOURED INK.
          This was a 400-weight blue or amber word on the card body. Neither has
          a light twin, so both kept their bright dark-mode value on a white
          card: the blue measured 1.9 and the amber 1.8 there, and nothing
          reports it — the class is in the DOM and the words are simply not
          there. A saturated `--fi-signal-*` fill under `--fi-signal-ink` is the
          documented answer and holds in both halves.

          IT IS IDENTITY, NOT STATUS, and it does not collide with one: the
          status lives on the card's 4px LEFT EDGE, this sits inside the card,
          and the two are never adjacent. The day number stays outside the badge
          in muted ink — it is not part of the phase's name. */}
      {cardType && (
        <div className="mb-2 flex items-center gap-1.5">
          <span className={`inline-flex items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[10px] font-black uppercase leading-none tracking-wider text-fi-signal-ink ${
            cardType === 'wash' ? 'bg-fi-signal-blue' : 'bg-fi-signal-amber'
          }`}>
            {cardType === 'wash' ? '🔫 WASH' : '🔧 RUN'}
          </span>
          {card?.isContinuation && <span className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">· Day {card.dayNumber}</span>}
        </div>
      )}
      {card && !isOverlay && (
        <PhaseClockRow
          card={card}
          pending={phaseClockMutation.isPending}
          onAction={(action) => phaseClockMutation.mutate({ jobId: job.id, phase: card.cardType, action, jobNumber: job.job_number })}
        />
      )}
      <div className="flex items-start justify-between mb-1">
        <div
          onClick={() => navigate(`/jobs/${job.id}`, { state: { from: '/dispatch' } })}
          className="flex items-start gap-2 flex-1 min-w-0"
        >
          <div className="min-w-0">
            <h4 className="font-bold text-white text-sm leading-tight group-hover:text-fieldiq-accent transition-colors">
              {job.customer?.name || 'No Client'}
            </h4>
            <p className="text-sm text-white font-medium mt-0.5">
              {job.job_number}
              {(job.rig_name || job.rig_number) && (
                <span> · {job.rig_name}{job.rig_number ? ` #${job.rig_number}` : ''}</span>
              )}
            </p>
            {job.job_type && (
              <p className="text-sm text-white font-medium">{getJobTypeLabel(job.job_type)}</p>
            )}
          </div>
        </div>
        <div className="relative flex-shrink-0" ref={menuRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
            className="text-fieldiq-muted transition hover:text-fieldiq-text p-1"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {crewPhaseOpen && <CrewPhaseDialog job={job} onClose={() => setCrewPhaseOpen(false)} />}
          {clearStartOpen && card && (
            <ClearPhaseStartConfirm
              jobId={job.id}
              jobNumber={job.job_number}
              phase={card.cardType}
              startedLabel={formatInstant(card.clock.startedAt, timeZone) ?? '—'}
              onClose={() => setClearStartOpen(false)}
            />
          )}
          {menuOpen && (
            <div className="absolute right-0 top-8 z-50 w-52 bg-fieldiq-card border border-fieldiq-border rounded-fi-panel shadow-xl py-2 animate-fade-in">
              {/* THE DESTRUCTIVE ITEM IS MARKED BY ITS GLYPH, NOT ITS WORD.
                  A 400-weight red label measures 3.1 on a white panel, and
                  `--fieldiq-danger` is not the fix either — it is 4.44 on white,
                  which is still under the floor. So the label keeps the body ink
                  every other item has and the hue sits on the icon and the
                  hover wash, which is the same answer CLAUDE.md gives for red
                  or green ink on a tint: let the wash and the icon carry it. */}
              {menuItems.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={(e) => { e.stopPropagation(); handleAction(key); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-fieldiq-text transition-colors ${
                    key === 'cancel' ? 'hover:bg-fieldiq-danger/15' : 'hover:bg-fieldiq-dark'
                  }`}
                >
                  <Icon className={`w-4 h-4 flex-shrink-0 ${key === 'cancel' ? 'text-fieldiq-danger' : 'text-fieldiq-muted'}`} />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div onClick={() => navigate(`/jobs/${job.id}`, { state: { from: '/dispatch' } })}>
        {job.service_type && (
          <p className="text-fieldiq-accent text-xs font-medium mb-2">{job.service_type}</p>
        )}

        {(() => {
          // A job with its own wash card shows each card the hands working that
          // phase; one card shows everyone. See lib/dispatch-crew-phase.ts.
          const crewSplit = card?.crewSplit === true;
          const crewToShow = sortCrew(crewForCard(job.crew, cardType, crewSplit));
          if (crewToShow.length === 0) return null;
          const cardIdForCrew = dragId || job.id;
          return (
          <div className="mb-2">
            <div className="flex flex-wrap gap-1">
              {crewToShow.map((member) => {
                if (isOverlay) {
                  const name = [member.first_name, member.last_name].filter(n => n && n.trim()).join(' ');
                  const initials = member.last_name?.trim()
                    ? `${member.first_name?.[0] || ''}${member.last_name[0]}`
                    : (member.first_name?.[0] || '');
                  return (
                    <span key={member.id} className={crewChipClass(member.specialty)}>
                      <CrewChipBadge specialty={member.specialty} />
                      {name || initials}
                    </span>
                  );
                }
                return (
                  <DraggableCrewChip
                    key={member.id}
                    crewMember={member}
                    cardId={cardIdForCrew}
                    jobId={job.id}
                    cardType={cardType}
                    split={crewSplit}
                  />
                );
              })}
            </div>
          </div>
        );})()}

        {job.equipment.length > 0 && (
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-3 h-3 text-fieldiq-muted" />
            <span className="text-xs text-fieldiq-text">
              {job.equipment.map(e => {
                const label = equipmentLabel(e.serial_number, e.name);
                return e.status ? `${label} (${equipmentStatusLabel(e.status)})` : label;
              }).join(', ')}
            </span>
          </div>
        )}

        {job.location_identifier && (
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-3 h-3 text-fieldiq-muted" />
            <span className="text-xs text-fieldiq-text">{job.location_identifier}</span>
          </div>
        )}

        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs text-fieldiq-muted">
              {job.tasks.total > 0
                ? `✓ ${job.tasks.completed}/${job.tasks.total} tasks complete`
                : 'No tasks'}
            </span>
          </div>
          {job.tasks.total > 0 && (
            <div className="h-1.5 bg-fieldiq-border rounded-full overflow-hidden">
              {/* `bg-fieldiq-accent-signal`, NOT `bg-fieldiq-accent`. Gold against
                  this track measures 1.35:1 in light — the filled portion was
                  invisible, and a task bar that cannot be read is worse than no
                  bar because it still looks like data. The signal token remaps to
                  #b45309 in light only (3.33:1) and is byte-identical gold in dark
                  (5.28:1). See src/styles/theme.css.

                  THE OTHER FOURTEEN BARS ARE SWEPT NOW. This comment used to list
                  nine and name the wrong ones — the list came from a grep piped
                  through `head`, so it was silently truncated at ten matches and
                  four of the sites it did name sit on a `bg-fieldiq-dark` track
                  rather than this one. The real population was fifteen across two
                  track tokens. `accent-signal.test.ts` scans for the shape now
                  instead of anybody maintaining a list here. */}
              <div
                className="h-full bg-fieldiq-accent-signal rounded-full transition-all"
                style={{ width: `${(job.tasks.completed / job.tasks.total) * 100}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * The phase clock on a day card: Started / Ended, and the one control that
 * moves it. Only the card `showsControls` names gets a button, so a run carried
 * across three days offers End once, on its last day, not three times.
 *
 * A carried-over run start is SAID to be carried over. It is the API's
 * inference from a pre-0403 started_at, and printing it like a recorded tap
 * would pass an inference off as a measurement.
 */
const PhaseClockRow: React.FC<{
  card: PhaseCard;
  pending: boolean;
  onAction: (action: 'start' | 'end') => void;
}> = ({ card, pending, onAction }) => {
  const { startedAt, endedAt, startCarriedOver } = card.clock;
  const started = startedAt != null;
  const label = card.cardType === 'wash' ? 'wash' : 'run';
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const timeZone = useTenantTimeZone();
  return (
    <div className="flex items-center justify-between gap-2 mb-2 text-xs" onPointerDown={stop} onClick={stop}>
      <span className="text-fieldiq-text min-w-0 truncate">
        {!started && <span className="text-fieldiq-muted">Not started</span>}
        {started && (
          <span>
            Started {formatInstant(startedAt, timeZone) ?? '—'}
            {startCarriedOver && <span className="text-fieldiq-muted"> (carried over)</span>}
          </span>
        )}
        {endedAt != null && <span> · Ended {formatInstant(endedAt, timeZone) ?? '—'}</span>}
      </span>
      {card.showsControls && (
        <button
          type="button"
          disabled={pending}
          onClick={() => onAction(started ? 'end' : 'start')}
          className="flex-shrink-0 px-2 py-1 rounded-md font-semibold bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent disabled:opacity-50"
        >
          {started ? `End ${label}` : `Start ${label}`}
        </button>
      )}
    </div>
  );
};

export default DispatchScreen;
